/*
 * Taxes on a stay. One function for the API and the desk, so the total a
 * guest is quoted is the total the booking saves.
 *
 * Rates are basis points (1200 = 12.00%). Each tax is charged on the price
 * before tax — room + meal plan + extras — not on another tax, and rounded to
 * the whole rupee on its own line.
 *
 * Exclusive (the default): listed prices are before tax and the taxes go on
 * top. Inclusive: listed prices already hold the taxes, so the guest pays the
 * listed price and the tax is worked out of it. ₹3,000 at 12% inclusive is
 * ₹321 GST on ₹2,679. Rounding lands in the pre-tax figure, never the total.
 */

export type TaxRate = { name: string; rateBp: number };
export type TaxLine = TaxRate & { amount: number };

export const taxLine = (base: number, t: TaxRate): TaxLine => ({ ...t, amount: Math.round((base * t.rateBp) / 10_000) });

/**
 * `subtotal` is the listed price: room + meals + extras as the desk sees them.
 * `net` is the price before tax (what the stay earns), `total` what the guest pays.
 */
export function applyTaxes(subtotal: number, taxes: readonly TaxRate[], inclusive = false): { lines: TaxLine[]; tax: number; net: number; total: number } {
  if (!inclusive) {
    const lines = taxes.map((t) => taxLine(subtotal, t));
    const tax = lines.reduce((s, l) => s + l.amount, 0);
    return { lines, tax, net: subtotal, total: subtotal + tax };
  }
  const rateBp = taxes.reduce((s, t) => s + t.rateBp, 0);
  const base = (subtotal * 10_000) / (10_000 + rateBp);
  const lines = taxes.map((t) => taxLine(base, t));
  const tax = lines.reduce((s, l) => s + l.amount, 0);
  return { lines, tax, net: subtotal - tax, total: subtotal };
}

/** The listed price a booking was priced from: before tax when exclusive, the all-in total when inclusive. */
export const listedAmount = (b: { amount: number; taxAmount: number; taxInclusive: boolean }): number => (b.taxInclusive ? b.amount : b.amount - b.taxAmount);

/** 1200 → "12%", 1250 → "12.5%". */
export function fmtRate(rateBp: number): string {
  return `${Number((rateBp / 100).toFixed(2))}%`;
}

/** "12.5" → 1250. Undefined when it isn't a rate between 0 and 100. */
export function parseRate(text: string): number | undefined {
  const n = Number(text.trim().replace(/%$/, ""));
  if (!Number.isFinite(n) || n <= 0 || n > 100) return undefined;
  return Math.round(n * 100);
}
